import { useState } from 'react'
import {
  useCommissionAccess,
  useCommissionAgents,
  useCommissionSetAgent,
  useCommissionCreatePolicy,
  useCommissionSetAccess,
} from '../../shared/api/generated/commercial-commissions/commercial-commissions'
import type { CommissionPolicyIn, CommissionPolicyOut } from '../../shared/api/generated/model'
import { errorMessage, percent, percentToBps, rateFields, rateMatrix, shortDate } from './helpers'
import { useInvalidateCommissions } from './hooks'
import './commissions.css'

/** Conteúdo do painel de um comercial: perfil, percentuais vigentes e nova vigência. */
export function AgentConfiguration({ userId }: { userId: number | null }) {
  const access = useCommissionAccess({ query: { retry: false } })
  const agents = useCommissionAgents({ query: { enabled: !!access.data?.can_manage } })
  const setAgent = useCommissionSetAgent()
  const setAccess = useCommissionSetAccess()
  const createPolicy = useCommissionCreatePolicy()
  const invalidate = useInvalidateCommissions()
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [rates, setRates] = useState<Record<string, string>>({})
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const agent = agents.data?.find((a) => a.user_id === userId)
  if (!access.data?.can_manage) return null
  if (userId == null)
    return (
      <p className="cc-muted">
        Após criar o acesso, configure aqui o perfil comercial e seus percentuais.
      </p>
    )
  const busy = setAgent.isPending || createPolicy.isPending || setAccess.isPending
  const policies = [...(agent?.policies ?? [])].sort((a, b) =>
    b.effective_from.localeCompare(a.effective_from),
  )
  const [latest, ...older] = policies
  const invalidKeys = new Set(
    rateFields
      .filter((f) => (rates[f.key] ?? '') !== '' && percentToBps(rates[f.key]) == null)
      .map((f) => f.key),
  )
  const savePolicy = async () => {
    const values = Object.fromEntries(
      rateFields.map((f) => [f.key, percentToBps(rates[f.key] ?? '')]),
    )
    if (!effectiveFrom || Object.values(values).some((v) => v == null)) {
      setFeedback({
        kind: 'error',
        text: 'Preencha a vigência e os seis percentuais entre 0 e 100. Zero é permitido.',
      })
      return
    }
    try {
      await createPolicy.mutateAsync({
        userId,
        data: { effective_from: effectiveFrom, ...values } as CommissionPolicyIn,
      })
      await invalidate()
      setFeedback({
        kind: 'ok',
        text: 'Nova vigência salva. Use a prévia do mês para reapurar o histórico.',
      })
      setRates({})
      setEffectiveFrom('')
    } catch (e) {
      setFeedback({ kind: 'error', text: errorMessage(e) })
    }
  }
  const prefillFromLatest = () => {
    if (!latest) return
    setRates(
      Object.fromEntries(
        rateFields.map((f) => [f.key, (latest[f.key] / 100).toLocaleString('pt-BR')]),
      ),
    )
  }
  return (
    <div className="cc-config">
      {agents.isError && (
        <p className="cc-notice cc-error" role="alert">
          {errorMessage(agents.error)}
        </p>
      )}
      <label className="cc-switch">
        <input
          type="checkbox"
          role="switch"
          aria-checked={agent?.active ?? false}
          checked={agent?.active ?? false}
          disabled={busy || agents.isLoading}
          onChange={async (e) => {
            try {
              await setAgent.mutateAsync({ userId, data: { active: e.target.checked } })
              await invalidate()
              setFeedback({
                kind: 'ok',
                text: e.target.checked
                  ? 'Perfil comercial ativado.'
                  : 'Perfil comercial desativado.',
              })
            } catch (error) {
              setFeedback({ kind: 'error', text: errorMessage(error) })
            }
          }}
        />
        <span className="cc-switch-track" aria-hidden="true" />
        <span>
          <strong>Agente comercial</strong>
          <small>
            {agent?.active
              ? 'Recebe comissão nas vendas e cirurgias em que for atribuído.'
              : 'Sem perfil ativo, vendas atribuídas a este usuário ficam pendentes.'}
          </small>
        </span>
      </label>

      {access.data.can_assign_access && (
        <section className="cc-drawer-section">
          <h3>Permissões de comissões</h3>
          <p className="cc-muted">
            Independentes do perfil de beneficiário. A gestão exige uma concessão explícita,
            inclusive para administradores do sistema.
          </p>
          {(
            [
              [
                'can_review',
                'Revisar atribuições de todos os deals',
                'Permite corrigir originador e fechador, inclusive no histórico, sem liberar valores de outros funcionários.',
              ],
              [
                'can_manage',
                'Gerir comissões',
                'Permite consultar todos os valores, configurar percentuais e aplicar apurações.',
              ],
            ] as const
          ).map(([key, label, hint]) => (
            <label className="cc-switch" key={key}>
              <input
                type="checkbox"
                role="switch"
                checked={agent?.[key] ?? false}
                aria-checked={agent?.[key] ?? false}
                disabled={busy || !agent || agents.isError}
                onChange={async (e) => {
                  const checked = e.target.checked
                  try {
                    await setAccess.mutateAsync({
                      userId,
                      data: {
                        can_review: agent?.can_review ?? false,
                        can_manage: agent?.can_manage ?? false,
                        [key]: checked,
                      },
                    })
                    await invalidate()
                    setFeedback({ kind: 'ok', text: 'Permissões atualizadas.' })
                  } catch (error) {
                    setFeedback({ kind: 'error', text: errorMessage(error) })
                  }
                }}
              />
              <span className="cc-switch-track" aria-hidden="true" />
              <span>
                <strong>{label}</strong>
                <small>{hint}</small>
              </span>
            </label>
          ))}
        </section>
      )}

      {agent?.active && (
        <>
          <section className="cc-drawer-section">
            <div className="cc-section-row">
              <h3>Percentuais vigentes</h3>
              {latest ? (
                <span className="cc-count">Desde {shortDate(latest.effective_from)}</span>
              ) : (
                <span className="cc-count is-warn">Não configurado</span>
              )}
            </div>
            {latest ? (
              <PolicyMatrix policy={latest} />
            ) : (
              <p className="cc-muted">
                Sem percentual vigente, as comissões deste comercial ficam pendentes até a primeira
                vigência ser salva.
              </p>
            )}
          </section>

          <section className="cc-drawer-section">
            <div className="cc-section-row">
              <h3>Nova vigência</h3>
              {latest && (
                <button type="button" className="cc-text-button" onClick={prefillFromLatest}>
                  Copiar vigentes
                </button>
              )}
            </div>
            <p className="cc-muted">
              Percentuais sobre os honorários médicos. Cada parcela usa a configuração vigente na
              data da venda ou da cirurgia. Vigências anteriores são preservadas.
            </p>
            <form
              className="cc-form"
              onSubmit={(e) => {
                e.preventDefault()
                void savePolicy()
              }}
            >
              <label className="cc-field cc-field-inline" htmlFor="cc-policy-from">
                Vigente desde
                <input
                  id="cc-policy-from"
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                />
              </label>
              <div
                className="cc-rate-grid"
                role="group"
                aria-label="Percentuais por papel e parcela"
              >
                <span />
                <span className="cc-rate-col">Venda</span>
                <span className="cc-rate-col">Operação</span>
                {rateMatrix.map((row) => (
                  <RateRow
                    key={row.role}
                    row={row}
                    rates={rates}
                    invalidKeys={invalidKeys}
                    onChange={(key, value) => setRates((v) => ({ ...v, [key]: value }))}
                  />
                ))}
              </div>
              <div className="cc-form-actions">
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {createPolicy.isPending ? 'Salvando…' : 'Salvar nova vigência'}
                </button>
              </div>
            </form>
          </section>
        </>
      )}

      {feedback && (
        <p
          className={`cc-notice${feedback.kind === 'error' ? ' cc-error' : ' cc-success'}`}
          role={feedback.kind === 'error' ? 'alert' : 'status'}
        >
          {feedback.text}
        </p>
      )}

      {older.length > 0 && (
        <details className="cc-history">
          <summary>Vigências anteriores ({older.length})</summary>
          <div className="cc-table-wrap">
            <table className="cc-table cc-history-table">
              <thead>
                <tr>
                  <th>Desde</th>
                  {rateFields.map((f) => (
                    <th key={f.key} className="cc-num">
                      {f.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {older.map((p) => (
                  <tr key={p.id}>
                    <td>{shortDate(p.effective_from)}</td>
                    {rateFields.map((f) => (
                      <td key={f.key} className="cc-num">
                        {percent(p[f.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  )
}

function PolicyMatrix({ policy }: { policy: CommissionPolicyOut }) {
  return (
    <div className="cc-rate-grid cc-rate-grid-readonly">
      <span />
      <span className="cc-rate-col">Venda</span>
      <span className="cc-rate-col">Operação</span>
      {rateMatrix.map((row) => (
        <div className="cc-rate-row" key={row.role}>
          <span className="cc-rate-role">
            {row.role}
            <small>{row.hint}</small>
          </span>
          <span className="cc-rate-value">{percent(policy[row.sale])}</span>
          <span className="cc-rate-value">{percent(policy[row.operated])}</span>
        </div>
      ))}
    </div>
  )
}

function RateRow({
  row,
  rates,
  invalidKeys,
  onChange,
}: {
  row: (typeof rateMatrix)[number]
  rates: Record<string, string>
  invalidKeys: Set<string>
  onChange: (key: string, value: string) => void
}) {
  return (
    <div className="cc-rate-row">
      <span className="cc-rate-role">
        {row.role}
        <small>{row.hint}</small>
      </span>
      {(['sale', 'operated'] as const).map((col) => {
        const key = row[col]
        const label = `${row.role} · ${col === 'sale' ? 'venda' : 'operação'}`
        return (
          <span className={`cc-rate-input${invalidKeys.has(key) ? ' is-invalid' : ''}`} key={key}>
            <input
              inputMode="decimal"
              aria-label={`${label} (%)`}
              aria-invalid={invalidKeys.has(key) || undefined}
              value={rates[key] ?? ''}
              onChange={(e) => onChange(key, e.target.value)}
            />
            <span aria-hidden="true">%</span>
          </span>
        )
      })}
    </div>
  )
}
